// The data window a person is about to request: which grains, which delivery day, and the
// [start, end] instants that day covers. It answers "may I submit?" for the whole page from one
// place: unsupported targets, loading, access errors and missing deliveries are statuses, not
// separate flags.
//
// Only what the person chose is state (requestedDay, requestedGranularity). Everything else is
// derived on each render from the registry and the availability store, so a country change, a
// refetch or a different grain never leaves a stale day behind and there are no reconcile effects.
import { useCallback, useMemo, useState } from 'react';
import { COUNTRIES } from '../config/countries';
import type { CountryCode } from '../config/countries';
import { PRODUCT_LABELS, availabilityTable, geoTypeLabel, supportStatus } from '../config/products';
import type { DataProductId, Granularity } from '../config/products';
import { datesFor, latest, pickDay } from '../utils/availability';
import { deliveryDayWindow, isIsoDay } from '../utils/dateWindow';
import type { IsoDay, TimeWindow } from '../utils/dateWindow';
import { clampRange } from '../utils/deliveryBars';
import { useAvailability } from './useAvailability';

export interface DataWindowArgs {
  country: CountryCode | null;
  product: DataProductId;
  geoType: string | null;
  /** Read once, on the first render (router state on return trips). Later changes are ignored. */
  initial?: { day?: IsoDay | null; granularity?: Granularity | null };
}

/** An ordered run of delivered-or-spanned days; start === end for one day. */
export interface DaySelection {
  start: IsoDay;
  end: IsoDay;
}

export type DataWindowStatus =
  | 'loading'
  | 'ready'
  | 'unsupported'
  | 'blocked'
  | 'no-dates'
  | 'no-access'
  | 'unavailable'
  | 'error';

export interface DataWindowState {
  status: DataWindowStatus;
  /** Why the status is not 'ready'; null while loading and when ready. */
  reason: string | null;
  /** Other countries where this product works; filled for 'unsupported' and 'blocked'. */
  alternatives: CountryCode[];
  retryable: boolean;
  retry(): void;
  /** Only grains whose table has at least one delivered day. Daily comes before hourly. */
  granularities: Granularity[];
  granularity: Granularity;
  setGranularity(g: Granularity): void;
  /** Delivered days of the chosen grain's table, ascending. */
  dates: readonly IsoDay[];
  /** The anchor day (first click). Unchanged meaning for single-day products. */
  day: IsoDay | null;
  setDay(d: IsoDay): void;
  /** Ordered span the window covers; start === end for one day; null unless there is a day. */
  selection: DaySelection | null;
  /** Choose a run of days: `anchor` stays as `day`, `other` is the far end (clamped, see maxRangeDays). */
  setRange(anchor: IsoDay, other: IsoDay): void;
  /** From the capability; 1 = single day only. */
  maxRangeDays: number;
  /** The instants the selection covers in the country's day zone for this grain; null unless there is a day. */
  window: TimeWindow | null;
  /** The most recent delivered day. */
  latest: IsoDay | null;
  /** The zone times are shown in. Windows can be cut on local days, but v1 displays UTC. */
  zoneLabel: 'UTC';
}

const GRAIN_ORDER: readonly Granularity[] = ['daily', 'hourly'];
const NO_DATES: readonly IsoDay[] = Object.freeze([]) as readonly IsoDay[];

const asGranularity = (g: unknown): Granularity | null => (g === 'hourly' || g === 'daily' ? g : null);

/**
 * Data window for one target. Pass `country: null` until it is known; the status is then
 * 'unsupported'. Nothing is fetched unless the country, product and area type are supported.
 */
export function useDataWindow({ country, product, geoType, initial }: DataWindowArgs): DataWindowState {
  const [requestedDay, setRequestedDay] = useState<IsoDay | null>(() => {
    const day = initial?.day;
    return isIsoDay(day) ? day : null;
  });
  const [requestedOther, setRequestedOther] = useState<IsoDay | null>(null);
  const [requestedGranularity, setRequestedGranularity] = useState<Granularity | null>(() =>
    asGranularity(initial?.granularity),
  );

  const support = useMemo(() => supportStatus(country, product, geoType), [country, product, geoType]);
  const { state: availability, retry } = useAvailability(support.ok ? country : null);

  const setGranularity = useCallback((g: Granularity) => setRequestedGranularity(g), []);
  const setDay = useCallback((d: IsoDay) => {
    setRequestedDay(d);
    setRequestedOther(null);
  }, []);
  // The derivation clamps the range, so the setter needs no capability check.
  const setRange = useCallback((anchor: IsoDay, other: IsoDay) => {
    setRequestedDay(anchor);
    setRequestedOther(other);
  }, []);

  return useMemo<DataWindowState>(() => {
    const base = {
      alternatives: [] as CountryCode[],
      retryable: false,
      retry,
      granularities: [] as Granularity[],
      dates: NO_DATES,
      day: null,
      setDay,
      selection: null,
      setRange,
      maxRangeDays: 1,
      setGranularity,
      window: null,
      latest: null,
      zoneLabel: 'UTC' as const,
    };

    if (!support.ok) {
      return {
        ...base,
        status: support.code === 'blocked' ? 'blocked' : 'unsupported',
        reason: support.reason,
        alternatives: support.alternatives,
        granularity: requestedGranularity ?? 'daily',
      };
    }

    // supportStatus refuses a null country, so a supported target always has one.
    const target = country as CountryCode;
    const countryLabel = COUNTRIES[target].label;
    const tableFor = (g: Granularity) => availabilityTable(target, product, geoType, g);
    const fallbackGranularity = support.capability.defaultGranularity;
    // Until the dates are known, show the grain the person asked for if this target serves it.
    const pendingGranularity =
      requestedGranularity && tableFor(requestedGranularity) ? requestedGranularity : fallbackGranularity;
    const waiting = (status: DataWindowStatus, reason: string | null, retryable = false): DataWindowState => ({
      ...base,
      status,
      reason,
      retryable,
      granularity: pendingGranularity,
    });

    switch (availability.status) {
      case 'idle':
      case 'loading':
        return waiting('loading', null);
      case 'no-access':
        return waiting('no-access', `Your account has no data access in ${countryLabel}.`);
      case 'unavailable':
        return waiting('unavailable', "Delivery days aren't served by this environment's API.");
      case 'error':
        return waiting('error', availability.message, availability.retryable);
      case 'ready':
        break;
    }

    const granularities = GRAIN_ORDER.filter((g) => datesFor(availability.index, tableFor(g)).length > 0);

    if (granularities.length === 0) {
      // The area type first, since its label may carry its own brackets ("Municipalities (omavalitsused) in Estonia").
      const where = support.geo ? `${geoTypeLabel(support.geo.geoType, target)} in ${countryLabel}` : countryLabel;
      // Not delivered yet and "your account may not have access" look the same from here: the
      // availability list only holds what the account can see.
      return waiting(
        'no-dates',
        `No ${PRODUCT_LABELS[product]} data for ${where} is available to your account. ` +
          'It may not be delivered yet, or your account may not have access.',
      );
    }

    const granularity =
      requestedGranularity && granularities.includes(requestedGranularity)
        ? requestedGranularity
        : granularities.includes(fallbackGranularity)
          ? fallbackGranularity
          : granularities[0];
    const dates = datesFor(availability.index, tableFor(granularity));
    const day = pickDay(dates, requestedDay);
    const maxRangeDays = support.capability.maxRangeDays ?? 1;
    const selection: DaySelection | null = day
      ? // A range belongs to the anchor the person chose; if that day is gone, fall back to one day.
        requestedOther && day === requestedDay
        ? clampRange(dates, day, requestedOther, maxRangeDays)
        : { start: day, end: day }
      : null;
    const zone = COUNTRIES[target].dayZone[granularity];

    return {
      ...base,
      status: 'ready',
      reason: null,
      granularities,
      granularity,
      dates,
      day,
      selection,
      maxRangeDays,
      window: selection
        ? {
            start_time: deliveryDayWindow(selection.start, zone).start_time,
            end_time: deliveryDayWindow(selection.end, zone).end_time,
          }
        : null,
      latest: latest(dates),
    };
  }, [support, country, product, geoType, availability, requestedDay, requestedOther, requestedGranularity, retry, setDay, setRange, setGranularity]);
}
